/**
 * useCustomerAlarms — React Query hook for the customer-facing global
 * Alarms hub at `/alarms`.
 *
 * Returns alarms across every site the caller has access to. Backing route:
 * `GET /api/v1/alarms` (Phase 5, EPIC #225 sub 6/10).
 *
 * Query key prefix is `['alarms', 'all', filters]` so multi-site WebSocket
 * invalidation (useGlobalAlarmSubscription) can match both this hook AND
 * the per-site list hook (`['alarms', siteId, …]`) without overlap.
 *
 * SOURCE SELECTION (#628)
 *
 * Unlike the site page, the hub's History bucket is served by the same
 * endpoint via `status=history` rather than a separate history call. But the
 * bucket is still a distinct QUERY, and the page previously collapsed its
 * multi-select status array to a scalar — yielding `undefined` for 2+ buckets,
 * which the server reads as "all active" and silently drops history.
 *
 * So the hub issues one request per selected source and merges, mirroring the
 * site page. This keeps the two alarm surfaces behaviourally identical.
 *
 * The hub's history request is served from `alarms.alarm_history` server-side
 * (#632) — cleared rows are purged from `alarms.active_alarms` ~60s after
 * clearing, so the bucket previously read an empty-by-design table and returned
 * nothing for every user. The server projects terminal `cleared` events into the
 * same `ActiveAlarm` shape this hook already merges, so no client-side
 * projection is needed here (unlike the site page, which projects in
 * `alarmsApi.listHistoryForSite`).
 */

import { useQuery } from '@tanstack/react-query';
import { alarmsApi, type CustomerListFilters } from '../services/alarmsApi';
import {
  mergeAlarmSources,
  serverStatusFilter,
  sourcesForStatuses,
} from '../components/alarms/merge-alarm-sources';
import type { ActiveAlarm } from '../types/alarm.types';

export interface CustomerAlarmsInput {
  /** Selected status buckets. Empty means "no bucket filter" → active only. */
  statuses: readonly string[];
  severity?: string;
  /**
   * ISO 8601 lower bound for raised_at. Applies to the HISTORY request only —
   * sending it on the active request would hide long-standing alarms
   * operators must not miss.
   */
  raised_after?: string;
}

export interface CustomerAlarmsResult {
  alarms: ActiveAlarm[];
  total: number;
  /**
   * True when the server capped the HISTORY response at its 1000-row limit.
   *
   * Deliberately named for the history portion rather than the whole list: the
   * merged `total` counts active rows too, so pairing "the 1,000 most recent"
   * with `total` would misdescribe a blended selection. Consumers must attach
   * any notice to the History portion, not the combined count (#639).
   */
  historyTruncated: boolean;
}

export function useCustomerAlarms(input: CustomerAlarmsInput) {
  const { statuses, severity, raised_after } = input;
  const { active, history } = sourcesForStatuses(statuses);
  const status = serverStatusFilter(statuses);

  const keyStatuses = [...statuses].sort().join(',');

  return useQuery<CustomerAlarmsResult>({
    queryKey: ['alarms', 'all', { statuses: keyStatuses, severity, raised_after }],
    queryFn: async () => {
      const activeFilters: CustomerListFilters = { status, severity };
      const historyFilters: CustomerListFilters = {
        status: 'history',
        severity,
        raised_after,
      };

      const [activeRes, historyRes] = await Promise.all([
        active ? alarmsApi.listAcrossSites(activeFilters) : null,
        history ? alarmsApi.listAcrossSites(historyFilters) : null,
      ]);

      const alarms = mergeAlarmSources(
        (activeRes?.alarms ?? []) as ActiveAlarm[],
        (historyRes?.alarms ?? []) as ActiveAlarm[],
      );

      return {
        alarms,
        total: alarms.length,
        // `?? false` normalises the optional wire field to a boolean so the
        // consumer branches on one shape. Absent means "not capped" — the
        // server only sets it on the history bucket, and only when the cap bit.
        historyTruncated: historyRes?.truncated ?? false,
      };
    },
    staleTime: 10_000,
  });
}
